import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faCircle,
  faExclamationCircle,
  faFileLines,
  faPen,
  faSpinner,
  faStop,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import CreateGoogleSheetButton from "../../components/CreateGoogleSheetButton";
import { Loading } from "../../components/ui";
import { getAvatarUrl } from "../../config/users";
import { UserAuth } from "../../context/AuthContext";
import { usePresence, useProjectCollaborators } from "../../context/PresenceContext";
import {
  getFromCache,
  setInCache,
  invalidateCache,
  CACHE_TTL,
  CACHE_KEYS,
} from "../../utils/cacheUtils";
import BreadCrumbs from "./BreadCrumbs";
import BulkNameUpload from "./BulkNameUpload";
import NameFilters from "./NameFilters";
import NamesTable from "./NamesTable";
import ProjectDetails from "./ProjectDetails";

const updateProgressFromStatus = status => {
  const progressStates = {
    complete: { create: true, generate: true, mutate: true, complete: true },
    generate: { create: true, generate: true, mutate: false, complete: false },
    mutate: { create: true, generate: true, mutate: true, complete: false },
    create: { create: true, generate: false, mutate: false, complete: false },
  };

  return progressStates[status] || { create: true, generate: false, mutate: false, complete: false };
};

const getStatusInfo = status => {
  if (!status) return { icon: faCircle, color: "text-base-content/30", label: "Draft" };
  const s = status.toString().toLowerCase().trim();
  if (s === "done")
    return { icon: faCheck, color: "text-success", label: "Complete" };
  if (["completed", "complete"].includes(s))
    return { icon: faCircle, color: "text-info", label: "Review" };
  if (["generate", "generating", "mutate", "mutating"].includes(s))
    return { icon: faSpinner, color: "text-primary", spin: true, label: "Processing" };
  if (["incomplete", "failed", "stopped"].includes(s))
    return { icon: faXmark, color: "text-error", label: "Stopped" };
  return { icon: faCircle, color: "text-base-content/30", label: "Draft" };
};

const formatDuration = (startedAt, completedAt) => {
  if (!startedAt) return null;

  const start = new Date(startedAt);
  const end = completedAt ? new Date(completedAt) : new Date();
  const diffMs = end - start;

  if (diffMs < 0) return null;

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

// Individual collaborator avatar with tooltip
const CollaboratorAvatar = ({ collab, index }) => {
  const avatarUrl = getAvatarUrl(collab.email) || collab.avatar;
  const firstName = collab.name?.split(" ")[0] || "User";

  return (
    <div className="group relative">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={collab.name}
          className="w-8 h-8 rounded-full border-2 border-base-100 object-cover animate-fade-in ring-0 group-hover:ring-2 ring-primary/30 transition-all cursor-default"
          referrerPolicy="no-referrer"
          style={{ animationDelay: `${index * 50}ms` }}
        />
      ) : (
        <div
          className="w-8 h-8 rounded-full border-2 border-base-100 bg-primary/10 flex items-center justify-center animate-fade-in ring-0 group-hover:ring-2 ring-primary/30 transition-all cursor-default"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <span className="text-primary text-xs font-medium">
            {collab.name?.charAt(0)?.toUpperCase() || "?"}
          </span>
        </div>
      )}
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-base-content text-base-100 text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
        {firstName}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-base-content" />
      </div>
    </div>
  );
};

// Collaborator avatars - shows who else is viewing this project
const Collaborators = ({ projectId, showSelf = false }) => {
  const collaborators = useProjectCollaborators(projectId);
  const { user } = UserAuth();

  // Build viewer list - include self if showSelf is true (for preview)
  let viewers = [...collaborators];
  if (showSelf && user?.email) {
    const selfViewer = {
      email: user.email,
      name: user.displayName || "You",
      avatar: getAvatarUrl(user.email, user.photoURL),
    };
    // Add self at the start if not already in list
    if (!viewers.find(v => v.email === user.email)) {
      viewers = [selfViewer, ...viewers];
    }
  }

  if (viewers.length === 0) return null;

  return (
    <div className="flex items-center gap-3 border-r border-base-content/10 pr-4 mr-2">
      <div className="flex items-center -space-x-2">
        {viewers.slice(0, 4).map((collab, idx) => (
          <CollaboratorAvatar key={collab.email} collab={collab} index={idx} />
        ))}
        {viewers.length > 4 && (
          <div className="w-8 h-8 rounded-full border-2 border-base-100 bg-base-content/10 flex items-center justify-center">
            <span className="text-base-content/60 text-[10px] font-medium">
              +{viewers.length - 4}
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-col">
        <span className="text-[10px] text-base-content/50">
          {viewers.length === 1 ? "Also viewing" : `${viewers.length} viewing`}
        </span>
      </div>
    </div>
  );
};

const Project = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const host = process.env.REACT_APP_SERVER;
  const { setViewingProject, clearViewingProject } = usePresence();

  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [nameData, setNameData] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [filters, setFilters] = useState({
    disregardF: true,
    disregardSensitive: true,
    maxCharacter: true,
    searchTerm: "",
  });
  const [generationStatus, setGenerationStatus] = useState({
    isGenerating: false,
    progress: { create: true, generate: false, mutate: false, complete: false },
    status: null,
    currentMessage: null,  // Real-time progress message from SSE
    modelResults: [],      // Results from each model
    mutationStats: null,   // Grade distribution for mutations
    resumeInfo: null,      // Resume info when continuing
  });
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const eventSourceRef = useRef(null);
  const [isStopping, setIsStopping] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [briefExpanded, setBriefExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [liveElapsed, setLiveElapsed] = useState(null);

  // Handlers
  const handleDelete = async () => {
    const confirmDelete = window.confirm(`Are you sure you want to permanently DELETE this project?`);
    if (confirmDelete) {
      try {
        const response = await fetch(`${host}/api/projects/${projectId}`, {
          method: "DELETE",
        });

        if (response.status === 204) {
          // Invalidate all caches for this project and projects list
          invalidateCache(CACHE_KEYS.projectsList());
          invalidateCache(CACHE_KEYS.projectDetail(projectId));
          invalidateCache(CACHE_KEYS.projectNames(projectId, projectData?.max_chars));
          invalidateCache(CACHE_KEYS.projectStats(projectId));

          navigate("/projects");
        } else {
          throw new Error("Failed to delete project");
        }
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleClear = async () => {
    const confirmClear = window.confirm("Are you sure you want to CLEAR all the names from this project?");
    if (confirmClear) {
      try {
        const response = await fetch(`${host}/api/projects/${projectId}/clear`, {
          method: "DELETE",
        });

        if (response.status === 204) {
          // Invalidate names and stats caches
          invalidateCache(CACHE_KEYS.projectNames(projectId, projectData?.max_chars));
          invalidateCache(CACHE_KEYS.projectStats(projectId));

          window.location.reload();
        } else {
          throw new Error("Failed to clear names");
        }
      } catch (error) {
        setError(error.message);
      }
    }
  };

  // Track presence - what project user is viewing
  useEffect(() => {
    if (projectData?.name && projectId) {
      setViewingProject(projectId, projectData.name);
    }

    return () => {
      clearViewingProject();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projectData?.name]);

  // Save scroll position
  useEffect(() => {
    const handleScroll = () => {
      sessionStorage.setItem("projectScrollPosition", window.scrollY.toString());
    };

    const savedPosition = sessionStorage.getItem("projectScrollPosition");
    if (savedPosition) {
      window.scrollTo(0, parseInt(savedPosition));
    }

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Query parameters
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      modelType: params.get("model"),
      mutationType: params.get("mutation"),
    };
  }, [location.search]);

  // Sort names
  const sortedNames = useMemo(() => {
    if (!sortColumn) return nameData;

    return [...nameData].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (sortColumn === "bookmarked") {
        const comparison = a.bookmarked === b.bookmarked ? 0 : a.bookmarked ? -1 : 1;
        return sortDirection === "asc" ? comparison : -comparison;
      }

      if (!aValue && !bValue) return 0;
      if (!aValue) return sortDirection === "asc" ? 1 : -1;
      if (!bValue) return sortDirection === "asc" ? -1 : 1;

      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [nameData, sortColumn, sortDirection]);

  // Apply filters
  const filteredNames = useMemo(() => {
    return sortedNames.filter(name => {
      if (filters.disregardF && ["D", "F"].includes(name.trademark)) return false;
      if (filters.disregardSensitive && name.sensitivity) return false;
      if (filters.maxCharacter && projectData?.max_chars && name.name.length > projectData.max_chars) return false;
      if (filters.searchTerm && !name.name.toLowerCase().includes(filters.searchTerm.toLowerCase())) return false;
      return true;
    });
  }, [sortedNames, filters, projectData?.max_chars]);

  // Fetch project data (with caching)
  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        // Check cache first
        const cachedProject = getFromCache(
          CACHE_KEYS.projectDetail(projectId),
          CACHE_TTL.PROJECT_DETAIL
        );

        let projectDataResult;

        if (cachedProject) {
          console.log("⚡ Using cached project data");
          projectDataResult = cachedProject;
        } else {
          const response = await fetch(`${host}/api/projects/${projectId}`);
          if (!response.ok) throw new Error("Failed to fetch project data");

          const { data } = await response.json();
          projectDataResult = data.project;

          // Cache the project data
          setInCache(CACHE_KEYS.projectDetail(projectId), projectDataResult);
        }

        setProjectData(projectDataResult);

        // Check if currently processing (backend still running)
        const status = projectDataResult.status?.toString().toLowerCase().trim();
        const isProcessing = ["generate", "generating", "mutate", "mutating"].includes(status);

        setGenerationStatus(prev => ({
          ...prev,
          status: projectDataResult.status,
          progress: updateProgressFromStatus(projectDataResult.status),
          isGenerating: isProcessing,
        }));
      } catch (error) {
        setError(error.message);
      }
    };

    fetchProjectData();
  }, [projectId, host]);

  // Fetch name data (with caching)
  useEffect(() => {
    const fetchNameData = async () => {
      if (!projectData) return;

      try {
        const maxChars = projectData.max_chars;

        // Check cache first
        const cachedNames = getFromCache(
          CACHE_KEYS.projectNames(projectId, maxChars),
          CACHE_TTL.NAMES_LIST
        );

        let namesResult;

        if (cachedNames) {
          console.log("⚡ Using cached names data");
          namesResult = cachedNames;
        } else {
          const maxCharsQuery = maxChars ? `?max_chars=${maxChars}` : "";
          const response = await fetch(`${host}/api/projects/${projectId}/names${maxCharsQuery}`);
          if (!response.ok) throw new Error("Failed to fetch name data");

          const { data } = await response.json();
          namesResult = data.names;

          // Cache the names data
          setInCache(CACHE_KEYS.projectNames(projectId, maxChars), namesResult);
        }

        // Apply query param filters (these are UI-level, not cached)
        let filteredNames = namesResult;
        if (queryParams.modelType) {
          filteredNames = filteredNames.filter(name => name.model === queryParams.modelType);
        }
        if (queryParams.mutationType) {
          filteredNames = filteredNames.filter(name => name.mutations?.[-1] === queryParams.mutationType);
        }

        setNameData(filteredNames);
        setIsLoading(false);
      } catch (error) {
        setError(error.message);
        setIsLoading(false);
      }
    };

    if (projectData) {
      fetchNameData();
    }
  }, [projectId, host, projectData, queryParams.modelType, queryParams.mutationType]);

  const handleSort = (column, direction) => {
    // If direction is provided (from SortButtons), use it directly
    // Otherwise toggle (from table header clicks)
    if (direction) {
      setSortColumn(column);
      setSortDirection(direction);
    } else if (sortColumn === column) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleGenerateNames = async () => {
    setGenerationStatus(prev => ({
      ...prev,
      isGenerating: true,
      currentMessage: "Starting generation...",
      modelResults: [],
      mutationStats: null,
      resumeInfo: null,
    }));

    // Close any existing SSE connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Connect to SSE for real-time updates
    const eventSource = new EventSource(`${host}/api/projects/${projectId}/events`);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener("stage", event => {
      const data = JSON.parse(event.data);
      setGenerationStatus(prev => ({
        ...prev,
        progress: updateProgressFromStatus(data.stage),
        status: data.stage,
      }));
    });

    eventSource.addEventListener("progress", event => {
      const data = JSON.parse(event.data);
      setGenerationStatus(prev => ({
        ...prev,
        currentMessage: data.message,
        progressPercent: data.progress,
        currentModel: data.current,
        totalModels: data.total,
        namesCount: data.namesCount,
        targetNames: data.targetNames
      }));
    });

    eventSource.addEventListener("modelComplete", event => {
      const data = JSON.parse(event.data);
      setGenerationStatus(prev => ({ ...prev, lastModelMessage: data.message }));
    });

    eventSource.addEventListener("info", event => {
      const data = JSON.parse(event.data);
      setGenerationStatus(prev => ({ ...prev, currentMessage: data.message }));
    });

    eventSource.addEventListener("lifecycle", event => {
      const data = JSON.parse(event.data);
      setGenerationStatus(prev => ({ ...prev, currentMessage: data.message }));
    });

    // Model completion with stats
    eventSource.addEventListener("modelStats", event => {
      const data = JSON.parse(event.data);
      console.log("[SSE] modelStats received:", data);
      setGenerationStatus(prev => ({
        ...prev,
        modelResults: data.allModels || [],
        currentMessage: `${data.model}: +${data.namesAdded} names`,
      }));
    });

    // Mutation phase stats
    eventSource.addEventListener("mutationStats", event => {
      const data = JSON.parse(event.data);
      setGenerationStatus(prev => ({
        ...prev,
        mutationStats: {
          gradeDistribution: data.gradeDistribution,
          mutationEligible: data.mutationEligible,
          totalNames: data.totalNames,
        },
      }));
    });

    // Resume summary when continuing from previous run
    eventSource.addEventListener("resumeSummary", event => {
      const data = JSON.parse(event.data);
      setGenerationStatus(prev => ({
        ...prev,
        resumeInfo: {
          skippedModels: data.skippedModels,
          remainingModels: data.remainingModels,
          existingNames: data.existingNames,
        },
        currentMessage: data.message,
      }));
    });

    eventSource.addEventListener("complete", event => {
      const data = JSON.parse(event.data);
      eventSource.close();
      eventSourceRef.current = null;

      if (data.status === "success") {
        setGenerationStatus(prev => ({
          ...prev,
          isGenerating: false,
          status: "complete",
          progress: { create: true, generate: true, mutate: true, complete: true },
          currentMessage: "Generation complete!",
        }));
        // Refresh after a short delay to show completion message
        setTimeout(() => window.location.reload(), 1000);
      } else {
        setGenerationStatus(prev => ({
          ...prev,
          isGenerating: false,
          currentMessage: data.message || "Generation ended",
        }));
      }
    });

    eventSource.addEventListener("error", event => {
      const data = event.data ? JSON.parse(event.data) : {};
      setGenerationStatus(prev => ({ ...prev, currentMessage: `Error: ${data.message || "Unknown error"}` }));
    });

    // Track connection failures
    let connectionFailures = 0;
    const maxFailures = 3;

    eventSource.onerror = () => {
      connectionFailures++;
      console.log(`SSE connection error (${connectionFailures}/${maxFailures})...`);

      // If connection is closed (server down), mark as stopped
      if (eventSource.readyState === EventSource.CLOSED || connectionFailures >= maxFailures) {
        console.log("SSE connection lost - marking generation as stopped");
        eventSource.close();
        eventSourceRef.current = null;
        setGenerationStatus(prev => ({
          ...prev,
          isGenerating: false,
          currentMessage: "Connection lost - generation may have stopped",
          status: prev.status === "generate" || prev.status === "mutate" ? "stopped" : prev.status,
        }));
      }
    };

    // Trigger generation (fire and forget - response comes via SSE)
    try {
      const response = await fetch(`${host}/api/projects/${projectId}/generate_names`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to initiate name generation");
      }
    } catch (error) {
      setError(error.message);
      setGenerationStatus(prev => ({ ...prev, isGenerating: false, currentMessage: null }));
      eventSource.close();
      eventSourceRef.current = null;
    }
  };

  // Cleanup SSE connection on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Live timer that updates every second during generation
  useEffect(() => {
    if (!generationStatus.isGenerating || !projectData?.generation_started_at) {
      setLiveElapsed(null);
      return;
    }

    const updateElapsed = () => {
      setLiveElapsed(formatDuration(projectData.generation_started_at, null));
    };

    updateElapsed(); // Initial update
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [generationStatus.isGenerating, projectData?.generation_started_at]);

  // Auto-refresh names every 30 seconds while generating
  useEffect(() => {
    if (!generationStatus.isGenerating) return;

    const interval = setInterval(() => {
      console.log("Auto-refreshing names...");
      refreshNames();
    }, 30000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationStatus.isGenerating]);

  const refreshNames = async () => {
    setIsRefreshing(true);

    // Invalidate caches to force fresh data
    invalidateCache(CACHE_KEYS.projectDetail(projectId));
    invalidateCache(CACHE_KEYS.projectNames(projectId, projectData?.max_chars));
    invalidateCache(CACHE_KEYS.projectStats(projectId));

    try {
      // Refresh project status
      const projectResponse = await fetch(`${host}/api/projects/${projectId}`);
      if (projectResponse.ok) {
        const { data } = await projectResponse.json();
        const status = data.project.status?.toString().toLowerCase().trim();
        const isProcessing = ["generate", "generating", "mutate", "mutating"].includes(status);

        // Re-cache the fresh data
        setInCache(CACHE_KEYS.projectDetail(projectId), data.project);

        setProjectData(data.project);
        setGenerationStatus(prev => ({
          ...prev,
          status: data.project.status,
          progress: updateProgressFromStatus(data.project.status),
          isGenerating: isProcessing,
        }));
      }

      // Refresh names
      const maxCharsQuery = projectData?.max_chars ? `?max_chars=${projectData.max_chars}` : "";
      const namesResponse = await fetch(`${host}/api/projects/${projectId}/names${maxCharsQuery}`);
      if (namesResponse.ok) {
        const { data } = await namesResponse.json();

        // Re-cache the fresh names
        setInCache(CACHE_KEYS.projectNames(projectId, projectData?.max_chars), data.names || []);

        setNameData(data.names || []);
      }
    } catch (error) {
      console.error("Failed to refresh:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleStopGeneration = async () => {
    const confirmStop = window.confirm(
      "Are you sure you want to stop the name generation? Any names already generated will be preserved."
    );
    if (!confirmStop) return;

    setIsStopping(true);

    try {
      const response = await fetch(`${host}/api/projects/${projectId}/stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 404 || response.status === 409) {
        // Process not found or already stopped - reset status in DB
        await fetch(`${host}/api/projects/${projectId}/status`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "stopped" }),
        });
        setGenerationStatus(prev => ({
          ...prev,
          isGenerating: false,
          status: "stopped",
        }));
      } else if (!response.ok) {
        throw new Error("Failed to stop generation");
      } else {
        setGenerationStatus(prev => ({
          ...prev,
          isGenerating: false,
          status: "stopped",
        }));
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsStopping(false);
    }
  };

  const handleStatusChange = async newStatus => {
    try {
      const response = await fetch(`${host}/api/projects/${projectId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error("Failed to update status");

      // Invalidate project cache since status changed
      invalidateCache(CACHE_KEYS.projectDetail(projectId));
      invalidateCache(CACHE_KEYS.projectsList());

      setGenerationStatus(prev => ({
        ...prev,
        status: newStatus,
        progress: updateProgressFromStatus(newStatus),
      }));
    } catch (error) {
      setError(error.message);
    }
  };

  const handleBookmarkToggle = async name => {
    // Optimistic update
    const updatedNames = nameData.map(n => (n.id === name.id ? { ...n, bookmarked: !n.bookmarked } : n));
    setNameData(updatedNames);

    // Update cache optimistically
    setInCache(CACHE_KEYS.projectNames(projectId, projectData?.max_chars), updatedNames);

    try {
      const response = await fetch(`${host}/api/projects/${projectId}/names/${name.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookmarked: !name.bookmarked }),
      });

      if (!response.ok) throw new Error("Failed to update bookmark");
    } catch (error) {
      // Revert on failure
      const revertedNames = nameData.map(n => (n.id === name.id ? { ...n, bookmarked: name.bookmarked } : n));
      setNameData(revertedNames);
      setInCache(CACHE_KEYS.projectNames(projectId, projectData?.max_chars), revertedNames);
      setError(error.message);
    }
  };

  const handleBulkUploadComplete = async result => {
    // Invalidate names cache immediately
    invalidateCache(CACHE_KEYS.projectNames(projectId, projectData?.max_chars));
    invalidateCache(CACHE_KEYS.projectStats(projectId));

    setTimeout(async () => {
      try {
        const maxCharsQuery = projectData.max_chars ? `?max_chars=${projectData.max_chars}` : "";
        const response = await fetch(`${host}/api/projects/${projectId}/names${maxCharsQuery}`);
        if (!response.ok) throw new Error("Failed to refresh name data");

        const { data } = await response.json();

        // Cache the fresh data
        setInCache(CACHE_KEYS.projectNames(projectId, projectData?.max_chars), data.names);

        setNameData(data.names);
      } catch (error) {
        console.error("Failed to refresh names after bulk upload:", error);
      }
    }, 5000);
  };

  if (isLoading) {
    return <Loading size="lg" message="Loading project..." fullScreen />;
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <BreadCrumbs projectId={projectId} projectName={projectData?.name} />

      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-base-100 rounded-lg p-4 sm:p-5 mb-6 border border-base-content/10">
        {/* Main row: Title + Primary Action */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: Back + Title + Status */}
          <div className="flex items-center gap-4 min-w-0">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h1 className="font-[Oswald] font-semibold tracking-tight uppercase leading-tight -skew-x-6 text-[1.75rem] text-[#555] truncate">
                  {projectData?.name}
                </h1>
                <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${
                  generationStatus.status === "done"
                    ? "bg-success/15 text-success"
                    : generationStatus.isGenerating
                    ? "bg-primary/15 text-primary"
                    : generationStatus.status === "complete"
                    ? "bg-info/15 text-info"
                    : ["incomplete", "failed", "stopped"].includes(generationStatus.status?.toLowerCase())
                    ? "bg-error/15 text-error"
                    : "bg-base-content/10 text-base-content/50"
                }`}>
                  {generationStatus.isGenerating && <FontAwesomeIcon icon={faSpinner} spin className="mr-1.5" />}
                  {generationStatus.isGenerating ? "Generating" : getStatusInfo(generationStatus.status).label}
                </span>
                {/* Duration display - only when generating or completed successfully */}
                {(generationStatus.isGenerating && liveElapsed) && (
                  <span className="text-xs text-primary font-medium">{liveElapsed}</span>
                )}
                {(!generationStatus.isGenerating && generationStatus.status === "done" && projectData?.generation_completed_at) && (
                  <span className="text-xs text-base-content/40">
                    {formatDuration(projectData.generation_started_at, projectData.generation_completed_at)}
                  </span>
                )}
              </div>
              {projectData?.client && (
                <span className="text-sm text-base-content/50 mt-0.5 block">{projectData.client}</span>
              )}
            </div>
          </div>

          {/* Right: Collaborators + Actions */}
          <div className="flex items-start gap-4 flex-shrink-0">
            {/* Collaborators viewing this project */}
            <Collaborators projectId={projectId} />

            <div className="flex items-center gap-2">
            {generationStatus.isGenerating ? (
              <button
                className="px-4 py-2 text-sm text-base-content/50 hover:text-error transition-colors"
                onClick={handleStopGeneration}
                disabled={isStopping}
              >
                {isStopping ? <FontAwesomeIcon icon={faSpinner} spin className="mr-1.5" /> : <FontAwesomeIcon icon={faStop} className="mr-1.5" />}
                Stop
              </button>
            ) : generationStatus.status === "done" ? (
              <>
                <CreateGoogleSheetButton
                  projectData={projectData}
                  projectId={projectId}
                  active={nameData.some(row => row.bookmarked)}
                />
                <button
                  onClick={() => handleStatusChange("complete")}
                  className="px-4 py-2 text-sm text-base-content/50 hover:text-base-content/70 transition-colors"
                >
                  Reopen
                </button>
              </>
            ) : (
              <>
                {/* Backend: Name input methods */}
                <div className="flex flex-col items-end">
                  <span className="text-[9px] uppercase tracking-wider text-base-content/30 mb-1">Add Names</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleGenerateNames}
                      className="px-3 py-1.5 text-sm text-base-content/50 hover:text-primary transition-colors"
                    >
                      {["incomplete", "failed", "stopped"].includes(generationStatus.status?.toLowerCase()) ? "Continue" : "Generate"}
                    </button>
                    <button
                      onClick={() => setShowBulkUpload(true)}
                      className="px-3 py-1.5 text-sm text-base-content/50 hover:text-primary transition-colors"
                    >
                      Upload
                    </button>
                  </div>
                </div>

                {/* Project management */}
                <div className="flex flex-col items-end border-l border-base-content/10 pl-4 ml-2">
                  <span className="text-[9px] uppercase tracking-wider text-base-content/30 mb-1">Project</span>
                  <div className="flex items-center gap-1">
                    {nameData.length > 0 && (
                      <button
                        onClick={() => handleStatusChange("done")}
                        className="px-3 py-1.5 text-sm text-base-content/50 hover:text-primary transition-colors"
                      >
                        Complete
                      </button>
                    )}
                    <button
                      onClick={handleClear}
                      className="px-3 py-1.5 text-sm text-base-content/50 hover:text-warning transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-3 py-1.5 text-sm text-base-content/50 hover:text-error transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </>
            )}
            </div>
          </div>
        </div>

        {/* Progress bar - only when generating */}
        {generationStatus.isGenerating && (
          <div className="mt-4">
            <div className="flex items-center gap-1">
              {["create", "generate", "mutate", "complete"].map((stage, idx) => (
                <div key={stage} className="flex items-center flex-1">
                  <div className={`flex-1 h-1 rounded-full transition-colors ${
                    generationStatus.progress[stage] ? "bg-primary" : "bg-base-content/10"
                  }`} />
                  {idx < 3 && <div className="w-1" />}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              {["Create", "Generate", "Mutate", "Complete"].map(label => (
                <span key={label} className={`text-[10px] ${
                  generationStatus.progress[label.toLowerCase()] ? "text-primary" : "text-base-content/30"
                }`}>
                  {label}
                </span>
              ))}
            </div>
            {/* Real-time progress display */}
            {(generationStatus.currentMessage || generationStatus.progressPercent !== undefined) && (
              <div className="mt-3 py-3 px-3 bg-base-content/5 rounded-lg space-y-2">
                {/* Progress bar */}
                {generationStatus.progressPercent !== undefined && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-base-content/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${generationStatus.progressPercent}%` }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-primary min-w-[3rem] text-right">
                      {generationStatus.progressPercent}%
                    </span>
                  </div>
                )}
                {/* Resume info banner */}
                {generationStatus.resumeInfo && generationStatus.resumeInfo.skippedModels > 0 && (
                  <div className="flex items-center justify-between text-[10px] py-1.5 px-2 bg-info/10 rounded text-info">
                    <span>⏩ Resuming: {generationStatus.resumeInfo.existingNames} names from {generationStatus.resumeInfo.skippedModels} completed models</span>
                    <span>{generationStatus.resumeInfo.remainingModels} models remaining</span>
                  </div>
                )}

                {/* Stats row */}
                {generationStatus.namesCount !== undefined && (
                  <div className="flex items-center justify-between text-[10px] text-base-content/50">
                    <span>
                      Model {generationStatus.currentModel || 0} / {generationStatus.totalModels || 0}
                    </span>
                    <span>
                      {generationStatus.namesCount?.toLocaleString()} / {generationStatus.targetNames?.toLocaleString()} names
                    </span>
                  </div>
                )}

                {/* Model Results */}
                {generationStatus.modelResults?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-base-content/10">
                    <div className="flex flex-wrap gap-1">
                      {generationStatus.modelResults.map((result, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-base-content/5 rounded text-[10px]"
                          title={result.model}
                        >
                          <span className="text-base-content/40 truncate max-w-[80px]">
                            {result.model.replace(/^get_|_names$/g, "").replace(/_/g, " ")}
                          </span>
                          <span className="text-primary font-medium">+{result.names_added}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mutation Stats - Grade Distribution */}
                {generationStatus.mutationStats && (
                  <div className="mt-2 pt-2 border-t border-base-content/10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-base-content/40">Grade Distribution</span>
                      <span className="text-[10px] text-primary font-medium">
                        {generationStatus.mutationStats.mutationEligible} eligible for mutation
                      </span>
                    </div>
                    <div className="flex gap-1">
                      {["A", "B", "C", "D", "F"].map(grade => {
                        const count = generationStatus.mutationStats.gradeDistribution?.[grade] || 0;
                        const total = generationStatus.mutationStats.totalNames || 1;
                        const pct = Math.round((count / total) * 100);
                        const colors = {
                          A: "bg-success/20 text-success",
                          B: "bg-info/20 text-info",
                          C: "bg-warning/20 text-warning",
                          D: "bg-error/20 text-error",
                          F: "bg-base-content/20 text-base-content/60",
                        };
                        return (
                          <div key={grade} className="flex-1 text-center">
                            <div className={`rounded py-1 ${colors[grade]}`}>
                              <div className="text-[10px] font-bold">{grade}</div>
                              <div className="text-[9px]">{count}</div>
                            </div>
                            <div className="text-[8px] text-base-content/30 mt-0.5">{pct}%</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Current action */}
                <p className="text-xs text-base-content/70 truncate flex items-center">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2 text-primary" />
                  {generationStatus.currentMessage}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Brief toggle - always visible */}
        <div className="mt-3 pt-3 border-t border-base-content/5">
          <button
            onClick={() => setBriefExpanded(!briefExpanded)}
            className="flex items-center gap-2 text-xs text-base-content/40 hover:text-base-content/60 transition-colors"
          >
            <FontAwesomeIcon icon={faFileLines} className="text-[10px]" />
            <span>Project Brief</span>
            <FontAwesomeIcon icon={briefExpanded ? faChevronUp : faChevronDown} className="text-[10px]" />
          </button>
        </div>

        {/* Brief content */}
        {briefExpanded && (
          <div className="mt-3 pt-3 border-t border-base-content/10">
            <ProjectDetails project={projectData} expanded />
            <Link
              to={`/projects/${projectId}/edit`}
              className="inline-flex items-center gap-1 mt-4 text-xs text-base-content/40 hover:text-primary transition-colors"
            >
              <FontAwesomeIcon icon={faPen} className="text-[10px]" />
              Edit Brief
            </Link>
          </div>
        )}

        {/* Bulk Upload */}
        {showBulkUpload && (
          <div className="mt-3 pt-3 border-t border-base-content/10">
            <BulkNameUpload
              projectId={projectId}
              countries={projectData?.countries || []}
              niceClasses={projectData?.nice_classes || []}
              onClose={() => setShowBulkUpload(false)}
              onComplete={handleBulkUploadComplete}
            />
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-base-100 rounded-lg p-4 border border-base-content/10 mb-4">
        <NameFilters
          filters={filters}
          onFilterChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          totalCount={nameData.length}
          filteredCount={filteredNames.length}
        />
      </div>

      {/* Names Grid/Table */}
      <div className="bg-base-100 rounded-lg p-4 border border-base-content/10 mb-4">
        <NamesTable
          names={filteredNames}
          projectId={projectId}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          onBookmark={handleBookmarkToggle}
          maxChars={projectData?.max_chars}
          viewMode={viewMode}
          onRefresh={refreshNames}
          isRefreshing={isRefreshing}
        />
      </div>

    </div>
  );
};

export default Project;
